/**
 * English, and the fallback for every other locale.
 *
 * Every key the portal uses must exist here — a missing key in `ja` or `zh` falls back
 * to English, but a missing key here renders as the key itself.
 */
const en = {
  Nav: {
    Home: 'Home',
    Dashboard: 'Dashboard',
    Account: 'Account',
    Cards: 'Cards',
    Keychip: 'Keychips',
    Import: 'Import',
    Announcements: 'Announcements',
    Contributors: 'Contributors',
    SignIn: 'Sign in',
    SignUp: 'Sign up',
    SignOut: 'Sign out',
    Games: 'Games',
    Theme: 'Theme',
    Language: 'Language',
  },
  Common: {
    Profile: 'Profile',
    Rating: 'Rating',
    PlayRecord: 'Play record',
    MusicList: 'Music list',
    Rival: 'Rivals',
    UserRanking: 'Player ranking',
    Setting: 'Settings',
    Loading: 'Loading…',
    Empty: 'Nothing here yet.',
    Error: 'Something went wrong.',
    Retry: 'Try again',
    Save: 'Save',
    Cancel: 'Cancel',
    Remove: 'Remove',
    Add: 'Add',
    Close: 'Close',
    Colon: ': ',
    Level: 'Level',
    Score: 'Score',
    PlayCount: 'Plays',
    LastPlay: 'Last played',
    Page: 'Page {{page}} of {{total}}',
    Previous: 'Previous',
    Next: 'Next',
    NoGameData: 'No save data for this game yet. Play a credit and it will appear here.',
    NoCard: 'Link a card first — cards are created by a cabinet, never by the portal.',
    Export: 'Export',
    Name: 'Name',
    Rank: 'Rank',
    Player: 'Player',
    Id: 'ID',
    Total: 'Total',
    Never: 'Never',
  },
  Auth: {
    Username: 'Username',
    Password: 'Password',
    Email: 'Email',
    DisplayName: 'Display name',
    UsernameOrEmail: 'Username or email',
    SignInTitle: 'Sign in',
    SignUpTitle: 'Create an account',
    SignInAction: 'Sign in',
    SignUpAction: 'Create account',
    NoAccount: 'No account yet?',
    HaveAccount: 'Already have an account?',
    ForgotPassword: 'Forgot your password?',
    ResetTitle: 'Reset your password',
    ResetAction: 'Reset password',
    SendCode: 'Send code',
    Code: 'Verification code',
    NewPassword: 'New password',
    TotpTitle: 'Two-factor authentication',
    TotpPrompt: 'Enter the six-digit code from your authenticator app.',
    TotpAction: 'Verify',
    SignedOut: 'You are signed out.',
  },
  Home: {
    Tagline: 'A network for CHUNITHM, maimai DX and O.N.G.E.K.I.',
    GetStarted: 'Get started',
    OpenDashboard: 'Open dashboard',
  },
  Dashboard: {
    Title: 'Dashboard',
    Games: 'Your games',
    NoGames: 'No game saves yet.',
    ServerRank: 'Server rank',
    TotalPlayers: 'of {{total}} players',
    Unranked: 'Unranked',
  },
  Account: {
    Title: 'Account',
    Roles: 'Roles',
    Joined: 'Joined',
    ChangePassword: 'Change password',
    CurrentPassword: 'Current password',
    Updated: 'Saved.',
    MustChange: 'Set a new password before you can use the rest of the site.',
    ConfirmPassword: 'Confirm new password',
    PasswordMismatch: 'The two passwords do not match.',
    PasswordRule: 'At least 8 characters.',
  },
  Admin: {
    Title: 'Admin',
    Forbidden: 'You do not have permission to view this page.',
    Players: 'Players',
    Search: 'Search',
    Status: 'Status',
    Active: 'Active',
    Banned: 'Banned',
    Ban: 'Ban',
    Unban: 'Unban',
    BanReasonPrompt: 'Reason for the ban (shown to the player):',
    Mail: 'Mail',
    MailHint:
      'Used for verification and password-reset codes. Unconfigured, codes are written to the server log instead.',
    MailEnabled: 'Send mail',
    MailHost: 'SMTP host',
    MailPort: 'Port',
    MailSecure: 'Implicit TLS (port 465)',
    MailUser: 'Username',
    MailPassword: 'Password',
    MailPasswordKept: 'unchanged',
    MailFrom: 'From',
    MailTestTo: 'Send a test to',
    MailTest: 'Test',
    MailTestSent: 'Test message sent.',
  },
  Cards: {
    Title: 'Cards',
    AccessCode: 'Access code',
    Registered: 'Registered',
    LastUsed: 'Last used',
    Default: 'Default',
    MakeDefault: 'Make default',
    Bind: 'Link a card',
    BindHint: 'Enter the 20-digit access code shown when a cabinet reads your card.',
    Unbind: 'Unlink',
    UnbindConfirm: 'Unlink this card? Its save data stays on the server.',
    Empty: 'No cards linked yet.',
  },
  Keychip: {
    Title: 'Keychips',
    Serial: 'Serial',
    Memo: 'Memo',
    Allowed: 'Allowed',
    Empty: 'No keychips registered.',
    Generate: 'Generate a keychip',
    Trusted: 'Trusted keychips',
  },
  Import: {
    Title: 'Import',
    Intro: 'Bring a save in from another server.',
    Unavailable: 'Importing is not available on this server yet.',
  },
  Announcements: {
    Title: 'Announcements',
    Empty: 'No announcements.',
    Posted: 'Posted',
  },
  Contributors: {
    Title: 'Contributors',
    Intro: 'QuiltNET is built on work published by these projects.',
  },
  Eula: {
    Title: 'Terms of use',
    Accept: 'I accept',
    Required: 'Please accept the terms of use to continue.',
  },
  Banned: {
    Title: 'Account suspended',
    Intro: 'This account cannot use the service.',
    Appeal: 'If you believe this is a mistake, contact the operator.',
  },
  Error: {
    PageTitle: 'This page ran into a problem',
    PageHint: 'The rest of the site still works. Try again, or pick another page.',
    Retry: 'Try again',
  },
  NotFound: {
    Title: 'Page not found',
    Intro: 'That page does not exist.',
    Home: 'Go home',
  },
  Chuni: {
    Title: 'CHUNITHM',
    Character: 'Characters',
    UserBox: 'User box',
    Nameplate: 'Nameplate',
    Trophy: 'Trophy',
    MapIcon: 'Map icon',
    SystemVoice: 'System voice',
    Stage: 'Stage',
    Avatar: 'Avatar',
    OverPower: 'Over power',
    Friend: 'Rivals',
  },
  Mai2: {
    Title: 'maimai DX',
    Photos: 'Photos',
    DxPass: 'DX pass',
    Circle: 'CiRCLE',
    Festa: 'Festa',
    ServerMissions: 'Server missions',
    PointExchanges: 'Point exchange',
    Deluxe: 'DX',
    Standard: 'STD',
  },
  Ongeki: {
    Title: 'O.N.G.E.K.I.',
    Battle: 'Battle',
    Card: 'Cards',
    CardGallery: 'Card gallery',
    MusicRanking: 'Music ranking',
    BattlePoint: 'Battle point',
    TechScore: 'Technical',
    BattleScore: 'Battle',
  },
} as const;

export default en;
